import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Mail, ShoppingBag, Check, Bell, Tag, RotateCcw } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DONE_KEY = 'cart_save_done';            // permanent nach erfolgreichem Speichern
const DISMISS_KEY = 'cart_save_dismissed_until';
const DISMISS_DAYS = 3;
const SHOW_DELAY_MS = 7000;                    // ~7s nach 1. Artikel (Wahl 1b)

// Pfade, auf denen das Popup NICHT erscheinen soll
const BLOCKED_PREFIXES = ['/admin', '/checkout', '/cart', '/auth', '/login', '/mein-konto'];

export default function CartSaveModal() {
  const { cart } = useCart();
  const { user, token } = useAuth();
  const location = useLocation();

  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const timerRef = useRef(null);
  const armedRef = useRef(false);

  const isGuest = !user && !token;
  const itemCount = cart?.item_count || 0;
  const onBlockedPath = BLOCKED_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Aktuelle Werte in Refs spiegeln, damit die Event-Listener stabil bleiben
  // (sonst löscht der Effect-Cleanup beim State-Update den Anzeige-Timer).
  const stateRef = useRef({ isGuest, itemCount, onBlockedPath });
  useEffect(() => {
    stateRef.current = { isGuest, itemCount, onBlockedPath };
  }, [isGuest, itemCount, onBlockedPath]);

  const eligible = useCallback(() => {
    const s = stateRef.current;
    if (!s.isGuest) return false;                     // Wahl 4: nur Gäste
    if (s.onBlockedPath) return false;
    if (localStorage.getItem(DONE_KEY)) return false;
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (Date.now() < until) return false;
    if (s.itemCount < 1) return false;
    return true;
  }, []);

  const openPopup = useCallback(() => {
    if (!eligible()) return;
    setShow(true);
    // Newsletter-Popup kurz unterdrücken, kein Doppel-Overlay
    localStorage.setItem('nl_dismissed_until', String(Date.now() + 24 * 60 * 60 * 1000));
  }, [eligible]);

  // Trigger 1b: ~7s nach dem ersten Hinzufügen + Trigger 1d: Exit-Intent.
  // Listener werden EINMAL registriert (deps []), Werte kommen aus Refs.
  useEffect(() => {
    const onAdd = () => {
      if (armedRef.current) return;
      armedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => openPopup(), SHOW_DELAY_MS);
    };
    const onMouseOut = (e) => {
      if (e.clientY > 0 || e.relatedTarget) return;
      if (stateRef.current.itemCount >= 1) openPopup();
    };
    window.addEventListener('trxl:cart-add', onAdd);
    document.addEventListener('mouseout', onMouseOut);
    return () => {
      window.removeEventListener('trxl:cart-add', onAdd);
      document.removeEventListener('mouseout', onMouseOut);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cart/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), consent: true }),
      });
      if (res.ok) {
        localStorage.setItem(DONE_KEY, '1');
        localStorage.setItem('nl_subscribed', '1'); // Newsletter-Popup dauerhaft unterdrücken
        setDone(true);
      }
    } catch (err) { /* silent */ }
    setLoading(false);
  };

  if (!show) return null;

  return (
    <div className="cs-overlay" data-testid="cart-save-popup" onClick={handleClose}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cs-close" onClick={handleClose} data-testid="cart-save-close" aria-label="Schließen">
          <X size={20} />
        </button>

        {done ? (
          <div className="cs-success" data-testid="cart-save-success">
            <div className="cs-icon-circle"><Check size={32} strokeWidth={3} /></div>
            <h3>Warenkorb gesichert!</h3>
            <p>Wir haben deinen Warenkorb für dich gespeichert. Du kannst jederzeit dort weitermachen, wo du aufgehört hast – und verpasst keine exklusiven Aktionen mehr.</p>
            <button className="cs-btn" onClick={handleClose} data-testid="cart-save-continue">Weiter einkaufen</button>
          </div>
        ) : (
          <>
            <div className="cs-icon-circle"><ShoppingBag size={28} strokeWidth={2} /></div>
            <h3>Warenkorb sichern &amp; nichts verpassen</h3>
            <p className="cs-sub">
              Trag deine E-Mail ein – wir speichern deine <strong>{itemCount} {itemCount === 1 ? 'Position' : 'Positionen'}</strong>,
              damit du später bequem weitermachen kannst.
            </p>

            <ul className="cs-benefits">
              <li><RotateCcw size={16} /> Warenkorb mit 1 Klick wiederherstellen</li>
              <li><Tag size={16} /> Exklusive Rabattaktionen &amp; Angebote</li>
              <li><Bell size={16} /> Erinnerung, bevor deine Teile vergriffen sind</li>
            </ul>

            <form onSubmit={handleSubmit} className="cs-form">
              <div className="cs-input-row">
                <Mail size={18} />
                <input
                  type="email"
                  placeholder="Deine E-Mail-Adresse"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="cart-save-email"
                />
              </div>
              <button type="submit" className="cs-btn" disabled={loading} data-testid="cart-save-submit">
                {loading ? 'Wird gespeichert...' : 'Warenkorb speichern'}
              </button>
            </form>

            <p className="cs-legal" data-testid="cart-save-consent">
              Mit dem Speichern willigst du ein, dass wir dich per E-Mail an deinen Warenkorb erinnern und dir Angebote senden dürfen.
              Du kannst dich jederzeit kostenlos abmelden. Details in unserer{' '}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>.
            </p>
          </>
        )}
      </div>

      <style>{`
        .cs-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.72);
          backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center;
          padding: 20px; animation: cs-fade 0.25s ease; }
        @keyframes cs-fade { from { opacity: 0; } to { opacity: 1; } }
        .cs-modal { position: relative; width: 100%; max-width: 420px;
          background: linear-gradient(160deg, #141414 0%, #0c0c0c 100%);
          border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 32px 28px 24px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,46,46,0.06);
          color: #fff; text-align: center; animation: cs-pop 0.3s cubic-bezier(0.16,1,0.3,1); }
        @keyframes cs-pop { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: none; } }
        .cs-close { position: absolute; top: 14px; right: 14px; background: rgba(255,255,255,0.05);
          border: none; color: rgba(255,255,255,0.6); width: 34px; height: 34px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .cs-close:hover { background: rgba(255,46,46,0.15); color: #fff; }
        .cs-icon-circle { width: 64px; height: 64px; margin: 4px auto 16px; border-radius: 50%;
          background: rgba(255,46,46,0.12); border: 1px solid rgba(255,46,46,0.3); color: #FF2E2E;
          display: flex; align-items: center; justify-content: center; }
        .cs-modal h3 { font-family: var(--font-heading, 'Barlow Condensed', sans-serif); font-size: 1.6rem;
          font-weight: 800; text-transform: uppercase; letter-spacing: -0.01em; margin: 0 0 8px; line-height: 1.1; }
        .cs-sub { color: rgba(255,255,255,0.62); font-size: 0.9rem; line-height: 1.55; margin: 0 0 18px; }
        .cs-sub strong { color: #fff; }
        .cs-benefits { list-style: none; padding: 0; margin: 0 0 20px; text-align: left; display: grid; gap: 10px; }
        .cs-benefits li { display: flex; align-items: center; gap: 10px; font-size: 0.86rem;
          color: rgba(255,255,255,0.8); }
        .cs-benefits li svg { color: #FF2E2E; flex-shrink: 0; }
        .cs-form { display: grid; gap: 12px; }
        .cs-input-row { display: flex; align-items: center; gap: 10px; background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 0 14px; transition: border-color 0.2s; }
        .cs-input-row:focus-within { border-color: rgba(255,46,46,0.55); }
        .cs-input-row svg { color: rgba(255,255,255,0.4); flex-shrink: 0; }
        .cs-input-row input { flex: 1; background: transparent; border: none; outline: none; color: #fff;
          font-size: 0.95rem; padding: 13px 0; }
        .cs-input-row input::placeholder { color: rgba(255,255,255,0.35); }
        .cs-btn { background: #FF2E2E; color: #fff; border: none; border-radius: 10px; padding: 14px 20px;
          font-family: var(--font-heading, 'Barlow Condensed', sans-serif); font-weight: 700; font-size: 1rem;
          text-transform: uppercase; letter-spacing: 0.04em; cursor: pointer; transition: background 0.2s, transform 0.1s; }
        .cs-btn:hover { background: #ff4646; }
        .cs-btn:active { transform: scale(0.98); }
        .cs-btn:disabled { opacity: 0.6; cursor: default; }
        .cs-legal { font-size: 0.7rem; color: rgba(255,255,255,0.38); line-height: 1.5; margin: 14px 0 0; }
        .cs-legal a { color: rgba(255,46,46,0.85); }
        .cs-success p { color: rgba(255,255,255,0.62); font-size: 0.9rem; line-height: 1.6; margin: 0 0 18px; }
        .cs-success .cs-icon-circle { background: rgba(46,204,113,0.12); border-color: rgba(46,204,113,0.4); color: #2ecc71; }
      `}</style>
    </div>
  );
}
